// FP2b — caller-identity hook for the surveys feature.
//
// GET /v1/me is the SSOT for the caller's roles, which drive the admin/manage
// affordances (the surveys list, launch form, template create, results view,
// pulse-cycle actions). The /v1/me route is fully typed (a `schema:` block
// declares the response), used here through the shared envelope-unwrap helper.
//
// ADVISORY ONLY (ADR-008/ADR-017): `canManageSurveys` / `canAdminSurveys`
// decide only what the UI SHOWS — the server independently gates every route
// (GET /v1/ee/surveys + /templates + /results are perm_ee_read; POST surveys/
// templates/pulse-cycles are perm_ee_admin). A forged/stale flag dangles a
// surface that then 403s, never widens authority.

import { useQuery } from "@tanstack/react-query";

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

/** The slice of /v1/me the surveys feature reads. */
export interface MeIdentity {
  readonly employee_id: string | null;
  readonly roles: readonly string[];
}

// ⚠ These two sets are a COPY of the server's gates in
// services/platform-graph/src/routes/surveys.ts (`EE_READ_ROLES` /
// `EE_ADMIN_ROLES`). They drifted, and the header's reasoning above only
// covers drift in the permissive direction ("dangles a surface that then
// 403s"). The drift that actually happened was the RESTRICTIVE one: both sets
// were missing role_hr_admin, so an İK Direktörü — whom GET /v1/ee/surveys
// answers 200 — was shown "you do not have access to this page". A copy that
// narrows authority is not advisory; it removes a surface the server grants.
// Any change to the server sets must land here in the same commit until the
// gate moves to a shared package.
const READ_ROLES: ReadonlySet<string> = new Set([
  "role_super_admin",
  "role_admin",
  "role_ee_admin",
  "role_ee_analyst",
  "role_recognition_moderator",
  "role_hr_admin",
  "role_manager",
]);

/** Roles that may ADMIN EE (launch surveys, create templates/pulse-cycles). */
const ADMIN_ROLES: ReadonlySet<string> = new Set([
  "role_super_admin",
  // role_admin is the server's intentional general-admin convenience grant.
  "role_admin",
  "role_ee_admin",
  "role_hr_admin",
]);

/** True iff any of the caller's roles may read EE results (the manage surface). */
export function canReadEeResults(roles: readonly string[]): boolean {
  return roles.some((r) => READ_ROLES.has(r));
}

/** True iff any of the caller's roles may admin EE (the create/launch surface). */
export function isEeAdmin(roles: readonly string[]): boolean {
  return roles.some((r) => ADMIN_ROLES.has(r));
}

type MeGet = (path: "/v1/me") => Promise<EnvelopedFetchResult<MeIdentity>>;

async function fetchMe(): Promise<MeIdentity> {
  const get = platformGraph.GET as unknown as MeGet;
  const data = await unwrapEnvelope(get("/v1/me"));
  return { employee_id: data.employee_id, roles: data.roles };
}

export interface MeIdentityState {
  readonly employeeId: string | null;
  readonly roles: readonly string[];
  /** May see the manage surface (surveys list, results, launch). */
  readonly canManageSurveys: boolean;
  /** May create/launch (templates, surveys, pulse-cycles). */
  readonly canAdminSurveys: boolean;
  readonly isLoading: boolean;
}

/**
 * Resolve the caller's own employee id + survey capabilities. While loading the
 * capability flags are FALSE (default-closed — the UI hides the manage/admin
 * surfaces until the real identity resolves, never flashes them).
 */
export function useMeIdentity(): MeIdentityState {
  const query = useQuery({
    queryKey: ["me", "identity"],
    queryFn: fetchMe,
    staleTime: 60_000,
  });
  const roles = query.data?.roles ?? [];
  return {
    employeeId: query.data?.employee_id ?? null,
    roles,
    canManageSurveys: canReadEeResults(roles),
    canAdminSurveys: isEeAdmin(roles),
    isLoading: query.isLoading,
  };
}
